<template>
  <global-dialog-card
    v-model="model"
    max-width="1000px"
    :title="$t('select_image')"
    hide-actions
  >
    <v-row align="end">
      <v-col cols="6">
        <text-field
          v-model="searchImage"
          :label="$t('image_search')"
          hide-details
          prepend-inner-icon="mdi-magnify"
        >
        </text-field>
      </v-col>
      <v-col cols="6" class="text-right">
        <input type="file" id="upload" hidden @change="upload" />
        <primary-button @click="$refs.fileInput.click()" :loading="uploading">
          <v-icon class="mr-2">mdi-plus-circle</v-icon>
          {{ uploading ? $t("uploading") : $t("add_image") }}
        </primary-button>
        <input type="file" ref="fileInput" hidden @change="upload" />
      </v-col>
    </v-row>

    <!-- Upload Progress Bar -->
    <v-row v-if="uploading">
      <v-col cols="12">
        <v-progress-linear v-model="uploadProgress" color="primary" height="25" reactive>
          <strong class="white--text">{{ Math.ceil(uploadProgress) }}%</strong>
        </v-progress-linear>
        <p class="text-center mt-2 mb-0">{{ $t("uploading_image") }}...</p>
      </v-col>
    </v-row>
    <v-row>
      <v-col
        v-for="image in filteredImages"
        :key="image.id"
        class="d-flex child-flex"
        cols="12"
        sm="6"
        md="4"
        lg="3"
      >
        <v-row>
          <v-col cols="12" class="pb-0">
            <v-img
              :src="getImageUrl(image)"
              @click="selectImage(image)"
              aspect-ratio="1"
              class="grey lighten-2"
            >
              <template v-slot:placeholder>
                <v-row class="fill-height ma-0" align="center" justify="center">
                  <v-progress-circular
                    indeterminate
                    color="grey lighten-5"
                  ></v-progress-circular>
                </v-row>
              </template>
            </v-img>
          </v-col>
          <v-col cols="12" class="pt-0 text-center">
            <div>{{ getCleanImageName(image.name) }}</div>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <!-- Empty State -->
    <empty-state
      v-if="filteredImages.length === 0 && !uploading"
      icon="mdi-image-off-outline"
      :title="searchImage ? $t('no_images_found') : $t('no_images_available')"
      :subtitle="
        searchImage
          ? `${$t('try_different_search')}: '${searchImage}'`
          : $t('upload_first_image')
      "
    >
      <primary-button
        v-if="!searchImage"
        @click="$refs.fileInput.click()"
        :loading="uploading"
      >
        <v-icon class="mr-2">mdi-plus-circle</v-icon>
        {{ $t("add_image") }}
      </primary-button>
    </empty-state>

    <global-dialog-card
      v-model="dialogWrongImageType"
      :max-width="500"
      hide-actions
      :title="$t('wrong_format')"
    >
      <p class="mt-4">
        {{ $t("wrong_image_format") }}
      </p>
    </global-dialog-card>
  </global-dialog-card>
</template>

<script>
import hasModel from "@/mixins/hasModel";
import hasSnackbar from "@/mixins/hasSnackbar";
import conf from "@/config";
import galleryService from "@/services/gallery";
import EmptyState from "@/components/global/EmptyState.vue";

export default {
  mixins: [hasModel, hasSnackbar],
  components: {
    EmptyState,
  },
  props: {
    meal: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      searchImage: "",
      images: [],
      dialogWrongImageType: false,
      uploading: false,
      uploadProgress: 0,
      conf,
    };
  },
  computed: {
    filteredImages() {
      if (!this.images || !Array.isArray(this.images)) {
        return [];
      }

      if (!this.searchImage || this.searchImage.trim() === "") {
        return this.images;
      }

      const searchTerm = this.searchImage.toLowerCase().trim();
      return this.images.filter((image) => {
        // Pretraga po imenu fajla
        const fileName = (image.name || "").toLowerCase();

        // Pretraga po čistom imenu (bez ekstenzije i prefiksa)
        const cleanName = this.getCleanImageName(image.name).toLowerCase();

        return fileName.includes(searchTerm) || cleanName.includes(searchTerm);
      });
    },
  },
  methods: {
    getImageUrl(image) {
      return `${this.conf.apiUrlBase}/gallery/${this.$store.state.restaurant.id}/medium/${image.name}`;
    },
    getCleanImageName(name) {
      if (typeof name === "string") {
        let imageName = name.split(".");
        let finalImage = imageName[0].split("_");
        return finalImage[1] || finalImage[0] || "";
      }
      return "";
    },
    getImage() {
      galleryService
        .getGalleryImages()
        .then((res) => {
          this.images = res.data;
        })
        .catch((error) => {
          this.showErrorSnackbar(error);
        });
    },
    filterImage() {
      this.imagesFilter = this.images.filter((i) => i.name.includes(this.searchImage));
    },

    selectImage(image) {
      this.meal.image_id = image.id;
      this.meal.image = image;
      this.model = false;
    },

    upload(event) {
      const file = event.target.files[0];
      if (!file) return;

      const allowedTypes = ["image/jpg", "image/jpeg", "image/png", "image/webp"];
      if (!allowedTypes.includes(file.type)) {
        this.dialogWrongImageType = true;
        setTimeout(() => {
          this.dialogWrongImageType = false;
        }, 5000);
        return;
      }

      this.uploading = true;
      this.uploadProgress = 0;

      let data = new FormData();
      data.append("image", file);

      galleryService
        .uploadGalleryImage(data, {
          onUploadProgress: (progressEvent) => {
            this.uploadProgress = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
          },
        })
        .then((res) => {
          this.images.push(res.data);
          this.selectImage(res.data);
          this.uploading = false;
          this.uploadProgress = 0;
          // Reset file input
          event.target.value = "";
          this.showSuccessSnackbar(this.$t("image_uploaded_successfully"));
        })
        .catch((error) => {
          this.showErrorSnackbar(error);
          this.uploading = false;
          this.uploadProgress = 0;
          // Reset file input
          event.target.value = "";
        });
    },

    closeDialogWrongType() {
      this.dialogWrongImageType = false;
    },
  },
  watch: {
    model(newVal) {
      if (newVal) {
        this.getImage();
      }
    },
  },
};
</script>
